import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInboundPackageListQuery } from 'api/warehouse';
import type { InboundPackage, InboundFilters } from 'models/Warehouse';
import { WAREHOUSE_ROUTES } from '../../../constants/routes';

interface UseInboundPackagesProps {
  searchValue?: string;
  filters?: InboundFilters;
}

export const useInboundPackages = ({ searchValue = '', filters = {} }: UseInboundPackagesProps) => {
  const [page, setPage] = useState(1);
  const [allPackages, setAllPackages] = useState<InboundPackage[]>([]);
  const pageSize = 10;
  const navigate = useNavigate();

  const { data, isLoading, isFetching } = useInboundPackageListQuery({
    page,
    size: pageSize,
    search: searchValue || undefined,
    filters,
  });

  // Stable reference for filters to use in dependency array
  const filtersRef = useMemo(() => JSON.stringify(filters), [filters]);

  // Reset state on mount
  useEffect(() => {
    setPage(1);
    setAllPackages([]);
  }, []);

  // Reset page and packages when search or filters change
  useEffect(() => {
    setPage(1);
    setAllPackages([]);
  }, [searchValue, filtersRef]);

  // Update all packages when new data arrives
  useEffect(() => {
    if (data?.packages) {
      setAllPackages((prev) => {
        if (page === 1) {
          return data.packages;
        }
        // Avoid duplicates
        const existingIds = new Set(prev.map((p) => p.id));
        const newPackages = data.packages.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newPackages];
      });
    }
  }, [data, page]);

  const hasMore = useMemo(() => {
    if (!data) return false;
    return allPackages.length < data.total;
  }, [allPackages.length, data]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  const handleCardClick = useCallback(
    (id: string) => {
      navigate(`${WAREHOUSE_ROUTES.inboundPackages}/${id}`);
    },
    [navigate]
  );

  const handleUploadDeliveryNote = useCallback(() => {
    navigate('/warehouse/upload-delivery-note');
  }, [navigate]);

  return {
    allPackages,
    isLoading,
    page,
    hasMore,
    loadMore,
    handleCardClick,
    handleUploadDeliveryNote,
  };
};
